import ApiService from '@/service'
import { alertUser, forceReload } from '@/utils/utils'
import { moduleActionContext } from '@/vuex'
import { defineActions, defineModule, defineMutations } from 'direct-vuex'

/**
 * New deployments of the application will be checked every `REFRESH_INTERVAL_MILLISECONDS`.
 * If a new version is found, an alert will prompt users to refresh
 * the browser to fetch changes.
 */
const REFRESH_INTERVAL_MILLISECONDS = 1000 * 30

export interface AppMetaState {
  firstSeenBuildTime?: string
  lastSeenBuildTime?: string
}

export const appMetaActionContext = (context: any) => moduleActionContext(context, appMetaModule)

const appMetaModule = defineModule({
  namespaced: true,

  state: {
    firstSeenBuildTime: undefined,
    lastSeenBuildTime: undefined,
  } as AppMetaState,

  mutations: defineMutations<AppMetaState>()({
    // This is for unit test usage, so one can setup test state without being mega-intrusive
    REPLACE(state: AppMetaState, payload: Partial<AppMetaState>) {
      const keys = Object.keys(payload) as (keyof AppMetaState)[]

      keys.forEach((key: keyof AppMetaState) => {
        state[key] = payload[key]
      })
    },

    SET_FIRST_SEEN_BUILD_TIME(state: AppMetaState, buildTime: string) {
      state.firstSeenBuildTime = buildTime
    },
    SET_LAST_SEEN_BUILD_TIME(state: AppMetaState, buildTime: string) {
      state.lastSeenBuildTime = buildTime
    },
  }),

  actions: defineActions({
    async POLL_BUILD_INFO(ctx): Promise<void> {
      const {
        dispatch,
        commit,
        state: { firstSeenBuildTime, lastSeenBuildTime },
      } = appMetaActionContext(ctx)

      try {
        const {
          data: { buildTime, forceRefresh },
        } = await ApiService.getBuildInfo(Math.floor(Math.random() * 99999999999))
        if (!firstSeenBuildTime) {
          commit.SET_FIRST_SEEN_BUILD_TIME(buildTime)
        }

        if (buildTime && lastSeenBuildTime && lastSeenBuildTime !== buildTime) {
          if (forceRefresh) {
            alertUser(
              'There is a new version of the frontend which is incompatible with the current backend and we have to reload your tab.'
            )
            forceReload()
            return
          } else {
            alertUser(
              'There is a new version of the frontend, and it is recommended you refresh this tab at your earliest convenience.'
            )
          }
        }

        commit.SET_LAST_SEEN_BUILD_TIME(buildTime)
        setTimeout(() => dispatch.POLL_BUILD_INFO(), REFRESH_INTERVAL_MILLISECONDS)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed requesting build info', err)
        setTimeout(() => dispatch.POLL_BUILD_INFO(), REFRESH_INTERVAL_MILLISECONDS)
      }
    },
  }),
})

export default appMetaModule
